/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import classNames from 'classnames';
import { Smartphone } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { ensureCurrentUser } from '../../../../util/data';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge
} from '../../../../components';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Events, mixPanelEventsData, mixPanelEventsScreens } from '../../../../mixpanel';
import { trackEventsMixpanel } from '../../../../mixpanel/mixpanel';
import { getCartCount } from '../../../CartPage/CartPage.duck';
import AuthIntentLink from '../../../../components/AuthIntentLink/AuthIntentLink';
import GetAppModal from '../GetAppModal/GetAppModal';
import LanguageToggle from '../LanguageToggle/LanguageToggle';
import css from './TopbarMobileMenu.module.css';

const lucideProps = {
  fill: 'none',
  'aria-hidden': true,
};

const getLocalizedCmsPath = (pathname, language) => {
  const isArabic = language === 'Arabic';

  if (pathname === '/p/about' || pathname === '/p/about-arabic') {
    return isArabic ? '/p/about-arabic' : '/p/about';
  }

  if (pathname === '/privacy-policy' || pathname === '/privacy-policy-arabic') {
    return isArabic ? '/privacy-policy-arabic' : '/privacy-policy';
  }

  return pathname;
};

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};
const arabicLanguage = typeof window !== 'undefined' && localStorage.getItem("language") === "Arabic";

const handleLanguageChange = (language, currentUserId, isCustomer, setSelectedLanguage) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('language', language);
    localStorage.setItem('rendered', language);
    setSelectedLanguage?.(language);
    const { pathname, search, hash } = window.location;
    const localizedPath = getLocalizedCmsPath(pathname, language);
    const nextUrl = `${localizedPath}${search}${hash}`;
    const currentUrl = `${pathname}${search}${hash}`;

    if (nextUrl !== currentUrl) {
      window.location.assign(nextUrl);
    } else {
      window.location.reload();
    }

    if (language && isCustomer) {
      trackEventsMixpanel(Events.CUSTOMER_SIGNUP_COMPLETE,
        {
          action_type: mixPanelEventsData.CUSTOMER_SETUP_LANGUAGE,
          screen: mixPanelEventsScreens.NAV_BAR,
          user_type: 'customer',
          user_id: currentUserId,
          selected_language: language?.toLowerCase()
        }
      );
    }
  }
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
  } = props;

  const intl = useIntl();
  const [getAppOpen, setGetAppOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState();

  useEffect(() => {
    const storedLanguage = typeof window !== 'undefined' && localStorage.getItem('language');
    if (storedLanguage) {
      setSelectedLanguage(storedLanguage);
    }
  }, []);

  const location = useLocation();
  const state = useSelector(state => state);
  const isOnCartPage = location.pathname === '/cart';
  const cartCount = getCartCount(state, isOnCartPage) || 0;

  const user = isAuthenticated ? ensureCurrentUser(currentUser) : null;

  const extraLinks = customLinks.map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user?.attributes?.profile?.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  const { userType, profileListingId } = currentUser?.attributes?.profile?.publicData || {};
  const isMerchant = userType === "provider";
  const isCustomer = userType === "customer";
  const currentUserId = currentUser?.id?.uuid;
  const shouldRedirectToMerchantPage = currentUserId && isMerchant && profileListingId;

  const bottomActions = (
    <div className={css.bottomActions}>
      <div className={css.bottomActionsRow}>
        <button
          type="button"
          className={css.getAppButton}
          onClick={() => setGetAppOpen(true)}
          aria-label={intl.formatMessage({ id: 'TopbarDesktop.getAppAriaLabel' })}
        >
          <span className={css.getAppIconWrap} aria-hidden="true">
            <Smartphone className={css.getAppIconSvg} size={16} strokeWidth={2} {...lucideProps} />
          </span>
          <span className={css.getAppLabel}>
            <FormattedMessage id="TopbarDesktop.getTheApp" />
          </span>
        </button>
        <LanguageToggle
          className={css.menuLanguageToggle}
          selectedLanguage={selectedLanguage}
          onToggle={lg => handleLanguageChange(lg, currentUserId, isCustomer, setSelectedLanguage)}
        />
      </div>
    </div>
  );

  const customerMenus = (<div className={css.accountLinksWrapper}>
    <NamedLink
      className={css.navigationLink}
      name="LandingPage"
    >
      <FormattedMessage id="TopbarDesktop.home" />
    </NamedLink>
    <NamedLink
      className={css.navigationLink}
      name="AlgoliaSearchPage"
      params={{ listingType: 'services' }}
    >
      <FormattedMessage id="TopbarDesktop.services" />
    </NamedLink>
    <NamedLink
      className={css.navigationLink}
      name="AlgoliaSearchPage"
      params={{ listingType: 'deals' }}
    >
      <FormattedMessage id="TopbarDesktop.deals" />
    </NamedLink>
    {isAuthenticated ? <NamedLink className={css.navigationLink} name="WishlistPage">
      <FormattedMessage id="TopbarDesktop.wishlist" />
    </NamedLink> : null}

    {!isAuthenticated ? (<NamedLink
      className={css.navigationLink}
      name="LandingPageMerchant"
      params={{ userType: 'provider' }}
    >
      <FormattedMessage id="TopbarDesktop.listYourBusiness" />
    </NamedLink>)
      : null}

    {isAuthenticated ? <>
      <NamedLink
        className={classNames(css.navigationLink, currentPageClass(shouldRedirectToMerchantPage ? "ViewBusinessPage" : "ProfilePage"))}
        name={shouldRedirectToMerchantPage ? "ViewBusinessPage" : currentUserId ? "ProfilePage" : "ProfileSettingsPage"}
        params={shouldRedirectToMerchantPage ? { listingType: "business-profile", id: profileListingId } : currentUserId ? { id: currentUserId } : {}}
      >
        <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
      </NamedLink>

      <NamedLink
        className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
        name="AccountSettingsPage"
      >
        <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
      </NamedLink>

      <NamedLink
        className={classNames(css.navigationLink, currentPageClass('NotificationPage'))}
        name="NotificationPage"
      >
        <FormattedMessage id="TopbarDesktop.notification" />
        {notificationCountBadge}
      </NamedLink>

      <NamedLink
        className={classNames(css.navigationLink, css.orderLink, (location?.pathname.includes("/sales/bookings") || location?.pathname.includes("/orders/bookings")) && css.activeLink)}
        name="InboxNewPage"
        params={{ tab: `${isMerchant ? 'sales' : 'orders'}`, state: 'bookings' }}
      >
        <FormattedMessage id="TopbarDesktop.bookings" />
      </NamedLink>
      <NamedLink
        className={classNames(css.navigationLink, css.orderLink, (location?.pathname.includes("/sales/orders") || location?.pathname.includes("/orders/orders")) && css.activeLink)}
        name="InboxNewPage"
        params={{ tab: `${isMerchant ? 'sales' : 'orders'}`, state: 'orders' }}
      >
        <FormattedMessage id="TopbarDesktop.orders" />
      </NamedLink>

      {/* <NamedLink
        className={classNames(
          css.navigationLink,
          currentPageClass('CartPage'),
        )}
        name="CartPage"
      >
        <FormattedMessage id="TopbarDesktop.yourCartLink" />
        <span className={css.cartCount}> {cartCount}</span>
      </NamedLink> */}
    </> : null}
  </div>)

  if (!isAuthenticated) {
    const signup = (
      <AuthIntentLink routeName="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </AuthIntentLink>
    );

    const login = (
      <AuthIntentLink routeName="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </AuthIntentLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );


    return (
      <div className={classNames(css.root, arabicLanguage && css.arabicDirection)}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          {customerMenus}

          {bottomActions}
        </div>
        <GetAppModal
          isOpen={getAppOpen}
          onClose={() => setGetAppOpen(false)}
          arabicLanguage={arabicLanguage}
        />
      </div>
    );
  }

  return (
    <div className={classNames(css.root, arabicLanguage && css.arabicDirection)}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        {isMerchant ?
          <div className={css.accountLinksWrapper}>
            <NamedLink
              className={css.navigationLink}
              name="CalenderPage"
            >
              <FormattedMessage id="TopbarDesktop.calendar" />
            </NamedLink>

            <NamedLink
              className={css.navigationLink}
              name="ViewListingsPage"
              params={{ listingType: 'deal' }}
            >
              <FormattedMessage id="TopbarDesktop.deals" />
            </NamedLink>
            <NamedLink
              className={classNames(css.navigationLink, css.orderLink, (location?.pathname.includes("/sales/orders") || location?.pathname.includes("/orders/orders")) && css.activeLink)}
              name="InboxNewPage"
              params={{ tab: 'sales', state: 'orders' }}
            >
              <FormattedMessage id="TopbarDesktop.orders" />
            </NamedLink>
            <NamedLink
              className={css.navigationLink}
              name="ViewListingsPage"
              params={{ listingType: 'service' }}
            >
              <FormattedMessage id="TopbarDesktop.services" />
            </NamedLink>
            {/* Products menu item - Hidden as not currently supported
            <NamedLink
              className={css.navigationLink}
              name="ViewListingsPage"
              params={{ listingType: 'product' }}
            >
              <FormattedMessage id="TopbarDesktop.products" />
            </NamedLink>
            */}
            <NamedLink className={css.navigationLink} name="NotificationPage">
              <FormattedMessage id="TopbarDesktop.notification" />
            </NamedLink>

            <NamedLink
              className={classNames(css.navigationLink, currentPageClass(shouldRedirectToMerchantPage ? "ViewBusinessPage" : "ProfilePage"))}
              name={shouldRedirectToMerchantPage ? "ViewBusinessPage" : currentUserId ? "ProfilePage" : "ProfileSettingsPage"}
              params={shouldRedirectToMerchantPage ? { listingType: "business-profile", id: profileListingId } : currentUserId ? { id: currentUserId } : {}}
            >
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>

            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>

            {/* <NamedLink
              className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
              name="InboxPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink> */}
            <NamedLink
              className={classNames(css.navigationLink, css.orderLink, (location?.pathname.includes("/sales/bookings") || location?.pathname.includes("/orders/bookings")) && css.activeLink)}
              name="InboxNewPage"
              params={{ tab: `${isMerchant ? 'sales' : 'orders'}`, state: 'bookings' }}
            >
              <FormattedMessage id="TopbarDesktop.bookings" />
            </NamedLink>
            <NamedLink className={css.navigationLink} name="MoreActionPage">
              <FormattedMessage id="TopbarDesktop.more" />
            </NamedLink>
            {/* <NamedLink
              className={classNames(css.navigationLink, currentPageClass('InboxNewPage'))}
              name="InboxNewPage"
              params={{ tab: `${isMerchant ? 'sales' : 'orders'}`, state: 'orders' }}
            >
              <FormattedMessage id="TopbarDesktop.orders" />
            </NamedLink> */}
            {/* <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink> */}

          </div>
          :
          customerMenus
        }
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        {bottomActions}
        <div className={css.spacer} />
      </div>
      <GetAppModal
        isOpen={getAppOpen}
        onClose={() => setGetAppOpen(false)}
        arabicLanguage={arabicLanguage}
      />
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
